import type { Product } from '../../../types/inventory';

/** Normalisation pour comparer des libellés (casse, espaces, accents). */
export function normalizeProductNameForMatch(s: string): string {
  const t = String(s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  return t;
}

function levenshteinDistance(a: string, b: string): number {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[m][n];
}

/** Score entre 0 et 1 (1 = identique après normalisation). */
export function nameSimilarityScore(aRaw: string, bRaw: string): number {
  const a = normalizeProductNameForMatch(aRaw);
  const b = normalizeProductNameForMatch(bRaw);
  if (!a.length && !b.length) return 1;
  if (!a.length || !b.length) return 0;
  if (a === b) return 1;
  const dist = levenshteinDistance(a, b);
  const denom = Math.max(a.length, b.length, 1);
  return Math.max(0, 1 - dist / denom);
}

export type RankedSourceCandidate = {
  id: string;
  name: string;
  barcode?: string;
  similarity: number;
};

// Seuil par défaut : on ne propose que des noms "vraiment" proches.
// 0.60 = 60% de similarité (Levenshtein normalisé).
const DEFAULT_MIN_SIMILARITY = 0.6;
// On garde une limite raisonnable pour l'UX (la recherche peut afficher beaucoup d'items).
const DEFAULT_LIMIT = 50;

/**
 * Classe les produits source par proximité de nom avec le libellé importé.
 */
export function rankSimilarSourceProducts(
  importName: string,
  products: Product[],
  options?: { limit?: number; minSimilarity?: number }
): RankedSourceCandidate[] {
  const limit = options?.limit ?? DEFAULT_LIMIT;
  const minSimilarity = options?.minSimilarity ?? DEFAULT_MIN_SIMILARITY;
  const name = String(importName ?? '').trim();
  if (!name) return [];

  const scored: RankedSourceCandidate[] = [];
  for (const p of products) {
    const similarity = nameSimilarityScore(name, p.name);
    if (similarity < minSimilarity) continue;
    scored.push({
      id: p.id,
      name: p.name,
      barcode: p.barcode,
      similarity,
    });
  }
  scored.sort((x, y) => y.similarity - x.similarity || x.name.localeCompare(y.name, 'fr'));
  return scored.slice(0, limit);
}

export type SourceMatchSelectOption = {
  value: string;
  label: string;
};

/**
 * Options pour un Select : mode automatique + produits similaires (triés).
 */
export function buildSourceMatchSelectOptions(
  importName: string,
  products: Product[],
  options?: { limit?: number; minSimilarity?: number }
): { auto: SourceMatchSelectOption; similar: SourceMatchSelectOption[] } {
  const auto: SourceMatchSelectOption = {
    value: '__auto__',
    label: 'Automatique (nom similaire ≥ 60 % dans le business référent)',
  };
  const similar = rankSimilarSourceProducts(importName, products, options).map((c) => ({
    value: c.id,
    label: `${c.name} — ${Math.round(c.similarity * 100)} %${c.barcode ? ` · ${c.barcode}` : ''}`,
  }));
  return { auto, similar };
}
